'use client';
import { useEffect, useState } from 'react';
import styles from './countdown.module.sass';
import cn from 'classnames';

interface CountDownTimerProps extends React.HTMLAttributes<HTMLDivElement> {
    timeStamp: number | string;
    labelDay: string;
    labelHour: string;
    labelMinute: string;
    labelSecond?: string;
    showSeconds?: boolean;
}

export const CountDownTimer = ({
    timeStamp,
    labelDay,
    labelHour,
    labelMinute,
    labelSecond,
    showSeconds,
    ...props
}: CountDownTimerProps) => {
    const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number }>({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    useEffect(() => {
        const target = new Date(timeStamp).getTime();

        const updateTimer = () => {
            const now = Date.now();
            const diff = target - now;

            if (diff <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            const totalMinutes = Math.floor(diff / 60000);
            const totalSeconds = Math.floor(diff / 1000);

            const days = Math.floor(totalMinutes / (60 * 24));
            const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
            const minutes = totalMinutes % 60;
            const seconds = totalSeconds % 60;

            setTimeLeft({ days, hours, minutes, seconds });
        };

        updateTimer(); // initial call
        const interval = setInterval(updateTimer, showSeconds ? 1000 : 10000); // update every 10 seconds

        return () => clearInterval(interval);
    }, [timeStamp, showSeconds]);

    return (
        <div className={cn(styles.holder, props.className)} {...props}>
            <div className={styles.time}>
                <strong>{timeLeft.days}</strong>
                <span>{labelDay}</span>
            </div>
            <div className={styles.time}>
                <strong>{timeLeft.hours}</strong>
                <span>{labelHour}</span>
            </div>
            <div className={styles.time}>
                <strong>{timeLeft.minutes}</strong>
                <span>{labelMinute}</span>
            </div>
            {showSeconds && labelSecond && (
                <div className={styles.time}>
                    <strong>{timeLeft.seconds}</strong>
                    <span>{labelSecond}</span>
                </div>
            )}
        </div>
    );
};
